import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_AVATAR_ID } from './defaultAvatars';

const STORAGE_KEY = 'goodtime.playerAvatarId';

/** Read the persisted avatar id once at module load. SSR-safe (returns
 * the registry default when {@code window} is missing). */
function readStored(): string | null {
  if (typeof window === 'undefined') return DEFAULT_AVATAR_ID;
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? DEFAULT_AVATAR_ID;
  } catch {
    return DEFAULT_AVATAR_ID;
  }
}

/** Module-level pub/sub so a write from one hook instance reaches every
 * other hook instance in the same tab.
 *
 * <p>Producer audit (Phase 2.5): the prior implementation only read the
 * initial localStorage value at mount and only synced via the browser's
 * `storage` event — which fires for OTHER tabs, not the originating
 * one. So when the onboarding picker called {@code setAvatarId('08')},
 * the world-map {@code <Avatar>} component (mounted earlier with id
 * 01) never saw the change until it remounted. This emitter notifies
 * every live hook instance synchronously on write. */
type Listener = (next: string | null) => void;
const listeners = new Set<Listener>();
function emit(next: string | null): void {
  for (const fn of listeners) fn(next);
}

/**
 * Read + write the player's chosen default-avatar id from localStorage.
 *
 * <p>v1 ships a 12-face commissioned default-avatar set
 * ({@link DEFAULT_AVATARS}) and persists the player's pick in
 * {@code goodtime.playerAvatarId}. When social profiles ship the
 * field will move server-side; this hook keeps the on-screen render
 * stable until then.
 *
 * @returns tuple of [currentId, setCurrentId]. Setting null resets to
 *   the registry default.
 */
export function usePlayerAvatar(): [string | null, (id: string | null) => void] {
  const [id, setId] = useState<string | null>(() => readStored());

  // Cross-instance sync (in-tab) + cross-tab sync (storage event).
  useEffect(() => {
    listeners.add(setId);
    if (typeof window === 'undefined') return () => {
      listeners.delete(setId);
    };
    const handler = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setId(event.newValue ?? DEFAULT_AVATAR_ID);
    };
    window.addEventListener('storage', handler);
    return () => {
      listeners.delete(setId);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const update = useCallback((next: string | null) => {
    if (typeof window !== 'undefined') {
      try {
        if (next === null) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Quota / private-mode failures — ignore, the in-memory state
        // still updates so the current session reflects the choice.
      }
    }
    // Notify EVERY hook instance (including this one) of the new
    // value. Doing it via the emitter rather than calling setId
    // directly keeps the write path single-source so a stray
    // update() from one component reaches the others without
    // remount.
    emit(next ?? DEFAULT_AVATAR_ID);
  }, []);

  return [id, update];
}
